// src/components/FruitAssignmentForm.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from "@mui/material";
import apiClient from "../utils/apiClient";
import "../style/Crops.css";
import MonthBoxGrid from "./MonthBoxGrid";

const monthNames = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"
];

const getMonthIndex = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date.getMonth();
};

const inRange = (start, end, month) => {
  if (start === null || end === null || month === null) return false;
  if (start <= end) return month >= start && month <= end;
  return month >= start || month <= end;
};

const FruitAssignmentForm = ({ open, onClose, plots, onSave, garden_uuid, planted, year, plantedCrops = [] }) => {
  const [formState, setFormState] = useState({
    plot_uuid: "",
    crop_uuid: "",
    date_planted: "",
    rows: "",
    method: "",
    orientation: "",
    placement_mode: "",
  });

  const [crops, setCrops] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [occupancy, setOccupancy] = useState([]);
  const [plotHasCrops, setPlotHasCrops] = useState(false);

  // Get dynamic columns based on fruit crop type's allowed_period_types
  const getFruitTableColumns = () => {
    if (crops.length === 0) return { headers: ['Name', 'Latin Name'], columns: ['name', 'latin_name'] };
    
    // Get the first fruit crop to determine the allowed period types
    const fruitCropType = crops[0]?.crop_type;
    if (!fruitCropType?.allowed_period_types) {
      return { headers: ['Name', 'Latin Name'], columns: ['name', 'latin_name'] };
    }

    const baseHeaders = ['Name', 'Latin Name'];
    const baseColumns = ['name', 'latin_name'];

    const periodTypeLabels = {
      'pre_germination': 'Pre-Germination',
      'direct_sowing': 'Direct Sowing', 
      'setting': 'Setting',
      'planting': 'Planting',
      'pruning': 'Pruning',
      'harvest': 'Harvest',
      'picking': 'Picking'
    };

    // Add only the period types that are allowed for fruit crop type
    fruitCropType.allowed_period_types.forEach(periodType => {
      if (periodTypeLabels[periodType]) {
        baseHeaders.push(periodTypeLabels[periodType]);
        baseColumns.push(periodType);
      }
    });

    // Always add the grid at the end
    baseHeaders.push('12-Month Grid');
    baseColumns.push('grid');

    return { headers: baseHeaders, columns: baseColumns };
  };

  // Filter plots to only show fruit tree plots that don't have fruit crops assigned
  const fruitTreePlots = plots.filter(plot => {
    // Must be a fruit tree plot
    if (plot.plot_type?.name?.toLowerCase() !== 'fruit tree') {
      return false;
    }
    
    // If editing an existing assignment, include the current plot
    if (planted && planted.plot_uuid === plot.plot_uuid) {
      return true;
    }
    
    // Check if this plot already has a fruit crop assigned
    const hasFruitCrop = plantedCrops.some(plantedCrop => 
      plantedCrop.plot_uuid === plot.plot_uuid &&
      plantedCrop.crop?.crop_type?.name?.toLowerCase() === 'fruit'
    );
    
    // Only include if it doesn't have a fruit crop assigned
    return !hasFruitCrop;
  });

  useEffect(() => {
    const fetchCrops = async () => {
      try {
        const response = await apiClient.get("/crops");
        // Filter crops to only show fruit types
        const fruitCrops = response.data.filter(crop => 
          crop.crop_type?.name?.toLowerCase() === 'fruit'
        );
        setCrops(fruitCrops);
      } catch (err) {
        console.error("Failed to fetch crops:", err);
      }
    };
    fetchCrops();
  }, []);

  useEffect(() => {
    if (planted) {
      setFormState({
        plot_uuid: planted.plot_uuid,
        crop_uuid: planted.crop_uuid,
        date_planted: planted.date_planted,
        rows: planted.rows || "",
        method: planted.method || "",
        orientation: planted.orientation || "",
        placement_mode: planted.placement_mode || "",
      });
    } else {
      setFormState({
        plot_uuid: "",
        crop_uuid: "",
        date_planted: "",
        rows: "",
        method: "",
        orientation: "",
        placement_mode: "",
      });
    }
  }, [planted]);

  useEffect(() => {
    const fetchOccupancy = async () => {
      if (formState.plot_uuid && formState.date_planted) {
        try {
          const res = await apiClient.get(
            `/plots/${formState.plot_uuid}/occupancy`,
            { params: { date: formState.date_planted } }
          );
          const occ = res.data.occupied || [];
          setOccupancy(occ);
          setPlotHasCrops(occ.length > 0);

          if (occ.length > 0 && occ[0].planted.orientation) {
            setFormState((prev) => ({
              ...prev,
              orientation: occ[0].planted.orientation,
            }));
          }
        } catch (err) {
          console.error("Failed to fetch occupancy:", err);
          setOccupancy([]);
          setPlotHasCrops(false);
        }
      } else {
        setOccupancy([]);
        setPlotHasCrops(false);
      }
    };
    fetchOccupancy();
  }, [formState.plot_uuid, formState.date_planted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const getWindowDates = (crop, periodType) => {
    const windows = (crop.crop_windows || []).filter(w => w.period_type === periodType);
    if (windows.length === 0) return { start: null, end: null };
    return {
      start: getMonthIndex(windows[0].window_start),
      end: getMonthIndex(windows[0].window_end)
    };
  };

  const handleCropSelect = (crop) => {
    console.log('handleCropSelect called with:', crop.name, crop.crop_uuid);
    
    const selectedMonth = formState.date_planted
      ? new Date(formState.date_planted).getMonth()
      : null;

    const planting = getWindowDates(crop, 'planting');
    const sowing = getWindowDates(crop, 'direct_sowing');

    const canPlant = inRange(planting.start, planting.end, selectedMonth);
    const canSow = inRange(sowing.start, sowing.end, selectedMonth);

    let newMethod = formState.method;
    if (selectedMonth !== null) {
      if (canPlant && !canSow) newMethod = "planting";
      else if (!canPlant && canSow) newMethod = "direct_sowing";
    }

    console.log('Setting crop_uuid to:', crop.crop_uuid);
    setFormState((prev) => {
      const newState = {
        ...prev,
        crop_uuid: crop.crop_uuid,
        method: newMethod,
      };
      console.log('New form state:', newState);
      return newState;
    });
  };

  const getMaxRows = () => {
    const plot = fruitTreePlots.find((p) => p.plot_uuid === formState.plot_uuid);
    const crop = crops.find((c) => c.crop_uuid === formState.crop_uuid);

    if (!plot || !crop || !formState.orientation) return null;

    const plotWidth = plot.unit === "meters" ? plot.width : plot.width * 0.3048;
    const plotDepth = plot.unit === "meters" ? plot.depth : plot.depth * 0.3048;

    const longSide = Math.max(plotWidth, plotDepth);
    const shortSide = Math.min(plotWidth, plotDepth);

    const rowSpacing = crop.row_spacing_cm
      ? crop.row_spacing_cm / 100
      : 0.25;

    const limitingDimension =
      formState.orientation === "lengthwise" ? shortSide : longSide;

    const maxRowsFull = Math.max(1, Math.floor(limitingDimension / rowSpacing));

    if (formState.placement_mode === "next_to" && occupancy.length > 0) {
      const usedRows = occupancy.reduce(
        (sum, occ) => sum + (occ.planted.rows || 0),
        0
      );
      return Math.max(0, maxRowsFull - usedRows);
    }

    return maxRowsFull;
  };

  const normalizePayload = (data) => {
    const normalized = { ...data };
    Object.keys(normalized).forEach((key) => {
      if (normalized[key] === "" || normalized[key] === null) {
        delete normalized[key];
      }
    });
    return normalized;
  };

  const isFormValid = () => {
    const valid = formState.plot_uuid && formState.crop_uuid;
    console.log('Form validation:', { 
      plot_uuid: formState.plot_uuid, 
      crop_uuid: formState.crop_uuid, 
      valid 
    });
    return valid;
  };

  const handleSubmit = async () => {
    console.log('handleSubmit called with formState:', formState);
    try {
      // Set defaults for fruit trees
      const payload = {
        plot_uuid: formState.plot_uuid,
        crop_uuid: formState.crop_uuid,
        date_planted: `${year}-01-01`,  // Jan 1 of current year
        method: "setting",               // Default for trees
        amount: 1                        // One tree
      };

      console.log('Submitting payload:', payload);

      if (planted && planted.planted_uuid) {
        await apiClient.put(
          `/gardens/${garden_uuid}/crops/${planted.planted_uuid}`,
          payload
        );
      } else {
        await apiClient.post(`/gardens/${garden_uuid}/crops/`, payload);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error("Failed to save fruit assignment:", err);
    }
  };

  const handleDelete = async () => {
    try {
      if (planted && planted.planted_uuid) {
        await apiClient.delete(
          `/gardens/${garden_uuid}/crops/${planted.planted_uuid}`
        );
        onSave();
        onClose();
      }
    } catch (err) {
      console.error("Failed to delete planted crop:", err);
    }
  };

  const selectedMonth = formState.date_planted
    ? new Date(formState.date_planted).getMonth()
    : null;

  const filteredCrops = crops.filter((crop) => {
    const planting = getWindowDates(crop, 'planting');
    const sowing = getWindowDates(crop, 'direct_sowing');
    const setting = getWindowDates(crop, 'setting');

    let valid = true;
    if (selectedMonth !== null) {
      if (formState.method === "planting") {
        valid = inRange(planting.start, planting.end, selectedMonth);
      } else if (formState.method === "direct_sowing") {
        valid = inRange(sowing.start, sowing.end, selectedMonth);
      } else if (formState.method === "setting") {
        valid = inRange(setting.start, setting.end, selectedMonth);
      } else {
        valid =
          inRange(planting.start, planting.end, selectedMonth) ||
          inRange(sowing.start, sowing.end, selectedMonth) ||
          inRange(setting.start, setting.end, selectedMonth);
      }
    }

    const matchesSearch = crop.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch && valid;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="FruitAssignmentForm">
      <DialogTitle>{planted ? "Edit Fruit Assignment" : "Assign Fruit to Tree"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth select
          label="Select Fruit Tree Plot"
          name="plot_uuid"
          value={formState.plot_uuid}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="">Select a fruit tree plot</MenuItem>
          {fruitTreePlots.map((plot) => (
            <MenuItem key={plot.plot_uuid} value={plot.plot_uuid}>
              {plot.name}
            </MenuItem>
          ))}
        </TextField>


        <Typography variant="h6" gutterBottom>
          Select a Fruit Variety ({filteredCrops.length} available)
          {formState.crop_uuid && (
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              ✓ Selected: {crops.find(c => c.crop_uuid === formState.crop_uuid)?.name}
            </Typography>
          )}
        </Typography>
        <TextField
          fullWidth
          label="Search Fruit Varieties"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, type, or Latin name"
          variant="outlined"
          margin="normal"
        />

        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            overflowX: 'auto',
            display: 'block',
            marginBottom: 2
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {getFruitTableColumns().headers.map((header, index) => (
                  <TableCell key={index}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCrops.map((crop) => {
                const preGerm = getWindowDates(crop, 'pre_germination');
                const sowing = getWindowDates(crop, 'direct_sowing');
                const planting = getWindowDates(crop, 'planting');
                const setting = getWindowDates(crop, 'setting');
                const pruning = getWindowDates(crop, 'pruning');
                const harvest = getWindowDates(crop, 'harvest');
                const picking = getWindowDates(crop, 'picking');
                
                const ranges = {
                  preGerminationStart: preGerm.start,
                  preGerminationEnd: preGerm.end,
                  directSowingStart: sowing.start,
                  directSowingEnd: sowing.end,
                  plantingStart: planting.start,
                  plantingEnd: planting.end,
                  settingStart: setting.start,
                  settingEnd: setting.end,
                  pruningStart: pruning.start,
                  pruningEnd: pruning.end,
                  harvestStart: harvest.start,
                  harvestEnd: harvest.end,
                  pickingStart: picking.start,
                  pickingEnd: picking.end,
                };

                const formatRange = (start, end) =>
                  start !== null && end !== null
                    ? `${monthNames[start]} to ${monthNames[end]}`
                    : "N/A";

                const renderCell = (column) => {
                  switch (column) {
                    case 'name':
                      return <TableCell>{crop.name}</TableCell>;
                    case 'latin_name':
                      return <TableCell>{crop.latin_name}</TableCell>;
                    case 'pre_germination':
                      return <TableCell>{formatRange(preGerm.start, preGerm.end)}</TableCell>;
                    case 'direct_sowing':
                      return <TableCell>{formatRange(sowing.start, sowing.end)}</TableCell>;
                    case 'setting':
                      return <TableCell>{formatRange(setting.start, setting.end)}</TableCell>;
                    case 'planting':
                      return <TableCell>{formatRange(planting.start, planting.end)}</TableCell>;
                    case 'pruning':
                      return <TableCell>{formatRange(pruning.start, pruning.end)}</TableCell>;
                    case 'harvest':
                      return <TableCell>{formatRange(harvest.start, harvest.end)}</TableCell>;
                    case 'picking':
                      return <TableCell>{formatRange(picking.start, picking.end)}</TableCell>;
                    case 'grid':
                      return (
                        <TableCell>
                          <MonthBoxGrid ranges={ranges} />
                        </TableCell>
                      );
                    default:
                      return <TableCell>-</TableCell>;
                  }
                };

                const isSelected = crop.crop_uuid === formState.crop_uuid;
                console.log('Row rendering:', {
                  cropName: crop.name,
                  cropUuid: crop.crop_uuid,
                  formStateCropUuid: formState.crop_uuid,
                  isSelected: isSelected
                });

                return (
                  <TableRow
                    key={crop.crop_uuid}
                    hover
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Table row clicked for crop:', crop.name);
                      handleCropSelect(crop);
                    }}
                    className={`crop-row ${isSelected ? 'crop-row-selected' : ''}`}
                  >
                    {getFruitTableColumns().columns.map((column, index) => (
                      renderCell(column)
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        {planted && planted.planted_uuid && <Button onClick={handleDelete} color="secondary">Delete</Button>}
        <Button onClick={onClose} color="secondary">Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!isFormValid()}
        >
          {planted ? "Update Fruit Assignment" : "Assign Fruit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FruitAssignmentForm;
